import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  UserDTOSchema,
  CreateUserSchema,
  UpdateUserSchema,
  UserRole,
} from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const UsersResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(UserDTOSchema),
  total: z.number().optional(),
});

export function useUsers({
  page,
  limit = 10,
  search,
}: {
  page: number;
  limit?: number;
  search?: string;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["users", page, search],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/users",
        params: { page, limit, ...(search ? { search } : {}) },
      });
      return UsersResponseSchema.parse(json);
    },
  });
}

export function useDoctors(options: { enabled?: boolean } = {}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["users-doctors"],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: "/api/v1/users" });
      return UsersResponseSchema.parse(json).data.filter(
        (u) => u.role === UserRole.Doctor,
      );
    },
    enabled: options.enabled !== false,
  });
}

export function useCreateUser() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateUserSchema>) =>
      backend({ method: "POST", url: "/api/v1/users", data }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: z.infer<typeof UpdateUserSchema>;
    }) =>
      backend({ method: "PATCH", url: `/api/v1/users/${id}`, data }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeleteUser() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/users/${id}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useActivateUser() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "PATCH", url: `/api/v1/users/${id}/activate` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeactivateUser() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "PATCH", url: `/api/v1/users/${id}/deactivate` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResendInvitation() {
  const backend = useBackendAuthenticated();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "POST", url: `/api/v1/users/${id}/resend-invitation` }),
  });
}

const ManagerDoctorsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
      specialityId: z.string().nullable().optional(),
      isActive: z.boolean(),
    }),
  ),
});

export function useManagerDoctors(managerId: string, options: { enabled?: boolean } = {}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["manager-doctors", managerId],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: `/api/v1/users/managers/${managerId}/doctors` });
      return ManagerDoctorsResponseSchema.parse(json).data;
    },
    enabled: options.enabled !== false && !!managerId,
  });
}

export function useAssignDoctor(managerId: string) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctorId: string) =>
      backend({ method: "POST", url: `/api/v1/users/managers/${managerId}/doctors`, data: { doctorId } }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["manager-doctors", managerId] }),
  });
}

export function useUnassignDoctor(managerId: string) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctorId: string) =>
      backend({ method: "DELETE", url: `/api/v1/users/managers/${managerId}/doctors/${doctorId}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["manager-doctors", managerId] }),
  });
}
